import { i18n } from '@quadralize/i18n'
import { cn, masks } from '@quadralize/utils'
import { Calendar as CalendarIcon } from '@solar-icons/react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import * as React from 'react'
import type { Matcher } from 'react-day-picker'
import { useTranslation } from 'react-i18next'
import { Calendar } from '../../data-display/calendar/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '../../overlays/popover/popover'
import { Button } from '../../ui/button/button'
import { Input } from '../input/input'
import { LabelWrapper } from '../label/label'

export interface DatePickerProps {
  date?: Date
  setDate?: (date?: Date) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  captionLayout?: 'label' | 'dropdown' | 'dropdown-months' | 'dropdown-years'
  label?: string
  id?: string
  required?: boolean
  error?: boolean
  editable?: boolean
  minYear?: number
  maxYear?: number
  helperText?: React.ReactNode
  minDate?: Date
  disablePast?: boolean
  isDateDisabled?: (date: Date) => boolean
  disabledDateMessage?: string
}

const DEFAULT_MIN_YEAR = 1900
const DEFAULT_MAX_YEAR = 2099

const COMPACT_THRESHOLD_WIDTH = 240
const DATE_MASK_LENGTH = 10
const DATE_PARTS_COUNT = 3

export function DatePicker({
  date,
  setDate,
  placeholder,
  className,
  disabled,
  captionLayout,
  label,
  id,
  required,
  error,
  editable,
  minYear = DEFAULT_MIN_YEAR,
  maxYear = DEFAULT_MAX_YEAR,
  helperText,
  minDate,
  disablePast,
  isDateDisabled,
  disabledDateMessage,
}: DatePickerProps) {
  const { t } = useTranslation('ui', { i18n })
  const generatedId = React.useId()
  const datePickerId = id || generatedId
  const [internalDate, setInternalDate] = React.useState<Date | undefined>(date)
  const containerRef = React.useRef<HTMLElement | null>(null)
  const [isCompact, setIsCompact] = React.useState(false)

  const [inputValue, setInputValue] = React.useState('')
  const [month, setMonth] = React.useState<Date>(date || new Date())
  const [typedErrorMessage, setTypedErrorMessage] = React.useState<string | undefined>()
  const resolvedPlaceholder = placeholder ?? t('datePicker.placeholder')
  const resolvedDisabledDateMessage = disabledDateMessage ?? t('datePicker.unavailableDate')
  const setButtonContainerRef = React.useCallback((node: HTMLButtonElement | null) => {
    containerRef.current = node
  }, [])
  const setEditableContainerRef = React.useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node
  }, [])

  const minDateValue = React.useMemo(() => {
    if (disablePast) {
      const d = new Date()
      d.setHours(0, 0, 0, 0)
      return d
    }
    return minDate
  }, [disablePast, minDate])

  const disabledMatchers = React.useMemo<Matcher[] | undefined>(() => {
    const matchers: Matcher[] = []

    if (minDateValue) {
      matchers.push({ before: minDateValue })
    }

    if (isDateDisabled) {
      matchers.push(isDateDisabled)
    }

    return matchers.length > 0 ? matchers : undefined
  }, [isDateDisabled, minDateValue])

  const selectedDate = date !== undefined ? date : internalDate

  React.useEffect(() => {
    if (selectedDate) {
      setInputValue(format(selectedDate, 'dd/MM/yyyy'))
      setMonth(selectedDate)
      setTypedErrorMessage(undefined)
    } else {
      setInputValue('')
    }
  }, [selectedDate])

  const onSelectDate = (newDate: Date | undefined) => {
    if (newDate && ((minDateValue && newDate < minDateValue) || isDateDisabled?.(newDate))) {
      setTypedErrorMessage(resolvedDisabledDateMessage)
      return
    }

    setInternalDate(newDate)
    setDate?.(newDate)
    if (newDate) {
      setInputValue(format(newDate, 'dd/MM/yyyy'))
      setTypedErrorMessage(undefined)
    } else {
      setInputValue('')
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const maskedValue = masks.date.format(e.target.value)
    setInputValue(maskedValue)

    if (maskedValue.length === DATE_MASK_LENGTH) {
      // Basic validation for complete date
      const parts = maskedValue.split('/').map(Number)
      if (parts.length === DATE_PARTS_COUNT) {
        const [day, monthVal, yearVal] = parts as [number, number, number]
        const newDate = new Date(yearVal, monthVal - 1, day)

        // Check if date is valid and year is reasonable
        if (
          newDate.getDate() === day &&
          newDate.getMonth() === monthVal - 1 &&
          newDate.getFullYear() === yearVal &&
          yearVal >= minYear &&
          yearVal <= maxYear
        ) {
          if ((minDateValue && newDate < minDateValue) || isDateDisabled?.(newDate)) {
            setTypedErrorMessage(resolvedDisabledDateMessage)
          } else {
            onSelectDate(newDate)
            setMonth(newDate)
            setTypedErrorMessage(undefined)
          }
        } else {
          if (yearVal < minYear || yearVal > maxYear) {
            setTypedErrorMessage(`O ano deve estar entre ${minYear} e ${maxYear}`)
          } else {
            setTypedErrorMessage(t('datePicker.invalidDate'))
          }
        }
      } else {
        setTypedErrorMessage(t('datePicker.invalidDate'))
      }
    } else {
      setTypedErrorMessage(undefined)
      if (maskedValue === '') {
        onSelectDate(undefined)
      }
    }
  }

  React.useEffect(() => {
    if (!containerRef.current) return

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Switch to compact format if width is less than the threshold
        setIsCompact(entry.contentRect.width < COMPACT_THRESHOLD_WIDTH)
      }
    })

    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <LabelWrapper
      label={label}
      id={datePickerId}
      required={required}
      error={error || !!typedErrorMessage}
      helperText={typedErrorMessage || helperText}
    >
      <Popover>
        <PopoverTrigger asChild>
          {editable ? (
            <div ref={setEditableContainerRef} className="relative">
              <Input
                id={datePickerId}
                placeholder={resolvedPlaceholder}
                value={inputValue}
                onChange={handleInputChange}
                disabled={disabled}
                error={error || !!typedErrorMessage}
                className={cn('pl-9', className)}
                autoComplete="off"
                inputMode="numeric"
                startIcon={<CalendarIcon className="h-4 w-4 shrink-0" />}
              />
            </div>
          ) : (
            <Button
              id={datePickerId}
              ref={setButtonContainerRef}
              variant={'outline'}
              size="md"
              justify="start"
              disabled={disabled}
              startIcon={<CalendarIcon className="h-4 w-4 shrink-0" />}
              className={cn(
                'w-full font-normal',
                error && 'border-error focus:ring-error',
                !selectedDate && 'text-text-tertiary',
                className,
              )}
            >
              <span className="truncate min-w-0">
                {selectedDate ? (
                  isCompact ? (
                    format(selectedDate, 'dd/MM/yyyy')
                  ) : (
                    format(selectedDate, 'PPP', { locale: ptBR })
                  )
                ) : (
                  <span>{resolvedPlaceholder}</span>
                )}
              </span>
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0"
          align="start"
          onOpenAutoFocus={editable ? (e) => e.preventDefault() : undefined}
        >
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={onSelectDate}
            captionLayout={captionLayout}
            month={month}
            onMonthChange={setMonth}
            minYear={minYear}
            maxYear={maxYear}
            disabled={disabledMatchers}
          />
        </PopoverContent>
      </Popover>
    </LabelWrapper>
  )
}
